import React, { useState, useEffect, useRef } from 'react';
import './MessageThreadModal.css';
import { getApiUrl } from '../../../config/api';
import { getAuthHeaders } from '../../../utils/auth';

// const getAuthHeaders = () => {
//   try {
//     const session = localStorage.getItem('userSession');
//     if (session) {
//       const token = JSON.parse(session)?.user?.token;
//       if (token) return { Authorization: `Bearer ${token}` };
//     }
//     const user = localStorage.getItem('user');
//     if (user) {
//       const token = JSON.parse(user)?.token;
//       if (token) return { Authorization: `Bearer ${token}` };
//     }
//   } catch {}
//   return {};
// };

const MessageThreadModal = ({ phoneNumber, customerName, appointmentId, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [customerPhotoUrl, setCustomerPhotoUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const isUserNearBottomRef = useRef(true);
  const shouldScrollOnUpdateRef = useRef(true);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check if user is scrolled near the bottom of the messages
  const checkIfNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const threshold = 100; // pixels from bottom
    const isNear = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    isUserNearBottomRef.current = isNear;
    return isNear;
  };

  // Decode URL-encoded messages (e.g., "Hi+I+need..." -> "Hi I need...")
  const decodeMessage = (message) => {
    if (!message) return '';
    try {
      // Replace + with spaces and decode URI components
      return decodeURIComponent(message.replace(/\+/g, ' '));
    } catch (error) {
      // If decoding fails, just replace + with spaces
      return message.replace(/\+/g, ' ');
    }
  };

  // Render message text with clickable links
  const renderMessageWithLinks = (text) => {
    if (!text) return null;

    // URL regex pattern to match http, https, and www links
    const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
    const parts = text.split(urlPattern);

    return parts.map((part, index) => {
      if (part.match(urlPattern)) {
        // Add https:// to www links if needed
        const href = part.startsWith('www.') ? `https://${part}` : part;
        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="message-link"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  useEffect(() => {
    // Only scroll to bottom if user was already near the bottom or explicitly requested
    if (shouldScrollOnUpdateRef.current || isUserNearBottomRef.current) {
      scrollToBottom();
    }
    shouldScrollOnUpdateRef.current = false;
  }, [messages]);

  useEffect(() => {
    console.log('🖼️ customerPhotoUrl state changed to:', customerPhotoUrl);
    console.log('🚫 imageError state is:', imageError);
  }, [customerPhotoUrl, imageError]);

  const fetchCustomerData = async () => {
    if (!phoneNumber) return;

    try {
      // Format phone number for search
      const cleanPhone = phoneNumber.replace(/\D/g, '');

      console.log('🔍 Fetching customer data for phone:', cleanPhone);

      // Search for customer by phone number using the 'term' parameter
      const searchUrl = getApiUrl(`/api/customers/search?term=${cleanPhone}`);
      const searchResponse = await fetch(searchUrl);

      if (searchResponse.ok) {
        const customers = await searchResponse.json();
        console.log('📋 Customer search results:', customers);

        if (customers && customers.length > 0) {
          const customer = customers[0];
          console.log('👤 Found customer ID:', customer.ID);

          // Store customer ID for profile link
          setCustomerId(customer.ID);

          // Fetch full customer details by ID to get CustomerPhotoUrl
          const customerDetailUrl = getApiUrl(`/api/customers/${customer.ID}`);
          const detailResponse = await fetch(customerDetailUrl);

          if (detailResponse.ok) {
            const customerDetails = await detailResponse.json();
            console.log('📄 Customer details:', customerDetails);

            if (customerDetails.CustomerPhotoUrl) {
              console.log('📸 Original photo URL:', customerDetails.CustomerPhotoUrl);

              // Convert Google Drive URL to CDN format to avoid CORS issues
              let photoUrl = customerDetails.CustomerPhotoUrl;

              if (photoUrl.includes('drive.google.com')) {
                // Extract file ID from Google Drive URL
                let fileId = null;

                if (photoUrl.includes('/uc?id=')) {
                  fileId = photoUrl.match(/id=([^&]+)/)?.[1];
                } else if (photoUrl.includes('/d/')) {
                  fileId = photoUrl.match(/\/d\/([^/]+)/)?.[1];
                } else if (photoUrl.includes('/file/d/')) {
                  fileId = photoUrl.match(/\/file\/d\/([^/]+)/)?.[1];
                }

                if (fileId) {
                  // Use Google's CDN URL which doesn't have CORS restrictions
                  photoUrl = `https://lh3.googleusercontent.com/d/${fileId}`;
                  console.log('📸 Converted to CDN URL:', photoUrl);
                }
              }

              setCustomerPhotoUrl(photoUrl);
              setImageError(false); // Reset image error state
            } else {
              console.log('⚠️ No CustomerPhotoUrl found for customer');
            }
          } else {
            console.log('❌ Failed to fetch customer details');
          }
        } else {
          console.log('⚠️ No customers found for phone number');
        }
      } else {
        console.log('❌ Search response not OK:', searchResponse.status);
      }
    } catch (err) {
      console.error('Error fetching customer data:', err);
      // Don't set error state - photo is optional
    }
  };

  const fetchMessageThread = async () => {
    if (!phoneNumber) {
      setError('No phone number provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Format phone number to E.164 format (remove dashes, spaces, add 1 if needed)
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const formattedPhone = cleanPhone.startsWith('1') ? cleanPhone : '1' + cleanPhone;

      console.log(`📞 Fetching messages for: ${formattedPhone}`);

      const url = getApiUrl(`/api/appointments/messages/${formattedPhone}`);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      console.log(`✅ Loaded ${data.messages?.length || 0} messages`);

      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching message thread:', err);
      setError('Failed to load message history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    shouldScrollOnUpdateRef.current = true; // Scroll to bottom on initial load
    fetchMessageThread();
    fetchCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  // Subscribe to SSE for real-time message updates (replaces 3-second polling)
  useEffect(() => {
    if (!phoneNumber) return;

    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const formattedPhone = cleanPhone.length === 10 ? '1' + cleanPhone : cleanPhone;

    let isInitialOpen = true;
    const source = new EventSource(getApiUrl(`/api/appointments/sse/messages/${formattedPhone}`));

    // On reconnect (not initial open) re-fetch the thread to catch anything missed
    source.onopen = () => {
      if (isInitialOpen) { isInitialOpen = false; return; }
      fetchMessageThread();
    };

    source.addEventListener('new_message', (e) => {
      const incoming = JSON.parse(e.data);
      shouldScrollOnUpdateRef.current = isUserNearBottomRef.current;
      setMessages(prev => {
        // Dedup: skip if a message with the same content+direction arrived within 2 seconds
        const isDup = prev.some(m =>
          m.OriginalMessage === incoming.OriginalMessage &&
          m.Direction === incoming.Direction &&
          Math.abs(new Date(m.SentDate) - new Date(incoming.SentDate)) < 2000
        );
        return isDup ? prev : [...prev, incoming];
      });
    });

    return () => source.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const extractAppointmentIdFromMessage = (message) => {
    const match = message?.match(/\[ID:(\d+)\]/i);
    return match ? match[1] : null;
  };

  const compressImage = (file) =>
    new Promise((resolve) => {
      const MAX_PX = 1200;
      const QUALITY = 0.75;
      const img = new Image();
      const blobUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(blobUrl);
        let { width, height } = img;
        if (width > MAX_PX || height > MAX_PX) {
          if (width > height) { height = Math.round(height * MAX_PX / width); width = MAX_PX; }
          else { width = Math.round(width * MAX_PX / height); height = MAX_PX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })),
          'image/jpeg',
          QUALITY
        );
      };
      img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(file); };
      img.src = blobUrl;
    });

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowed = files.filter(f => /^image\/(jpeg|png|gif)$/.test(f.type));
    if (allowed.length !== files.length) {
      setError('Only JPG, PNG, and GIF images are supported.');
    }

    const compressed = await Promise.all(allowed.map(compressImage));
    setAttachedFiles(prev => [...prev, ...compressed].slice(0, 10));
    const previews = compressed.map(f => URL.createObjectURL(f));
    setFilePreviews(prev => [...prev, ...previews].slice(0, 10));
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    URL.revokeObjectURL(filePreviews[index]);
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachedFiles.length === 0) return;
    if (!phoneNumber) return;

    setSending(true);
    setError(null);
    try {
      const url = getApiUrl('/api/appointments/send-sms');

      let response;
      if (attachedFiles.length > 0) {
        const formData = new FormData();
        formData.append('appointmentId', appointmentId || 0);
        formData.append('phoneNumber', phoneNumber);
        formData.append('message', newMessage);
        formData.append('messageType', '3');
        attachedFiles.forEach(file => formData.append('files', file));

        response = await fetch(url, {
          method: 'POST',
          headers: { ...getAuthHeaders() },
          body: formData
        });
      } else {
        response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            appointmentId: appointmentId || 0,
            phoneNumber: phoneNumber,
            message: newMessage,
            messageType: '3'
          })
        });
      }

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setNewMessage('');
      filePreviews.forEach(url => URL.revokeObjectURL(url));
      setAttachedFiles([]);
      setFilePreviews([]);
      shouldScrollOnUpdateRef.current = true;
    } catch (err) {
      setError('Failed to send message: ' + err.message);
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleImageError = (e) => {
    console.log('Image failed to load:', customerPhotoUrl);

    // Try Google Drive alternative URL format
    if (customerPhotoUrl && customerPhotoUrl.includes('/uc?id=')) {
      const fileId = customerPhotoUrl.match(/id=([^&]+)/)?.[1];
      if (fileId && !imageError) {
        console.log('Trying alternative Google Drive URL format');
        e.target.src = `https://lh3.googleusercontent.com/d/${fileId}`;
        setImageError(true); // Prevent infinite loop
        return;
      }
    }

    // If all fails, hide image and show default icon
    e.target.style.display = 'none';
    setImageError(true);
  };

  const handleOpenProfile = () => {
    if (customerId) {
      const profileUrl = `/customers/${customerId}`;
      window.open(profileUrl, '_blank');
    }
  };

  return (
    <div className="message-thread-overlay" onClick={onClose}>
      <div className="message-thread-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="message-thread-header">
          <div className="modal-header-content">
            <div className="customer-avatar-wrapper">
              {customerPhotoUrl && !imageError ? (
                <img
                  src={customerPhotoUrl}
                  alt={customerName || 'Customer'}
                  className="customer-avatar-image"
                  loading="lazy"
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  onError={handleImageError}
                />
              ) : (
                <div className="customer-avatar-default">
                  <i className="fas fa-user"></i>
                </div>
              )}
            </div>
            <div className="customer-info">
              <h2>{customerName || 'Customer'}</h2>
              <p className="phone-display">{phoneNumber}</p>
            </div>
          </div>
          <div className="header-actions">
            {customerId && (
              <button
                onClick={handleOpenProfile}
                className="profile-link-btn"
                title="View customer profile"
              >
                <i className="fas fa-external-link-alt"></i>
              </button>
            )}
            <button onClick={onClose} className="close-modal-btn">✕</button>
          </div>
        </div>

        {/* Messages Container */}
        <div className="messages-container" ref={messagesContainerRef} onScroll={checkIfNearBottom}>
          {loading && (
            <div className="loading-messages">
              <div className="spinner"></div>
              <p>Loading conversation...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && messages.length === 0 && (
            <div className="no-messages">
              <p>No message history found</p>
              <small>Send a message to start the conversation</small>
            </div>
          )}

          {!loading && !error && messages.length > 0 && (
            <div className="messages-list">
              {messages.map((msg, index) => {
                // Backend returns field as "Message" not "OriginalMessage"
                const rawMessageText = msg.Message || msg.OriginalMessage || '';
                const messageText = decodeMessage(rawMessageText);
                const msgAppointmentId = extractAppointmentIdFromMessage(messageText);
                const isSent = msg.Direction === 'sent';

                return (
                  <div
                    key={msg.ID || index}
                    className={`message-bubble-wrapper ${isSent ? 'sent' : 'received'}`}
                  >
                    <div className={`message-bubble ${isSent ? 'sent' : 'received'}`}>
                      {msg.MediaUrl && (
                        <div className="message-media">
                          <img
                            src={msg.MediaUrl}
                            alt="MMS attachment"
                            className="message-media-img"
                            onClick={() => window.open(msg.MediaUrl, '_blank')}
                          />
                        </div>
                      )}
                      {messageText ? (
                        <div className="message-content">
                          <p>{renderMessageWithLinks(messageText)}</p>
                        </div>
                      ) : null}
                      <div className="message-meta">
                        <span className="message-time">{formatTimestamp(msg.SentDate)}</span>
                        {msgAppointmentId && (
                          <span className="message-appointment-id">Appt #{msgAppointmentId}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="message-thread-footer">
          {filePreviews.length > 0 && (
            <div className="attachment-previews">
              {filePreviews.map((src, i) => (
                <div key={i} className="attachment-preview-item">
                  <img src={src} alt={`attachment ${i + 1}`} className="attachment-thumbnail" />
                  <button
                    className="remove-attachment-btn"
                    onClick={() => removeAttachment(i)}
                    title="Remove image"
                  >✕</button>
                </div>
              ))}
            </div>
          )}
          <div className="message-input-container">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button
              className="attach-image-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || attachedFiles.length >= 10}
              title="Attach image (JPG, PNG, GIF)"
            >
              <i className="fas fa-image"></i>
            </button>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={attachedFiles.length > 0 ? 'Add a caption (optional)...' : 'Type your message...'}
              className="message-input"
              disabled={sending}
              rows={2}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <button
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && attachedFiles.length === 0) || sending}
              className="send-message-btn"
            >
              {sending ? 'Sending...' : (attachedFiles.length > 0 ? 'Send MMS' : 'Send')}
            </button>
          </div>
          <div className="footer-info">
            <span className="message-count">
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </span>
            <button onClick={fetchMessageThread} className="refresh-messages-btn" title="Refresh messages">
              ↻
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageThreadModal;
